// /admin/pedidos-b2b — Workspace de gestión comercial B2B.
// Solo pedidos con empresa_id (compra_unica) en estados activos: borrador, pendiente_pago, en_armado.
// Los B2C tienda no aparecen (van directo al embudo de entregas cuando pagan).

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { adminMarcarPedidoPagado, adminCancelarPedido } from '../../lib/portalClienteApi'
import { labelVentana } from '../../lib/ventanasHorarias'

const ESTADO_PILL = {
  borrador:       { bg: '#F1F5F9', color: '#475569', label: 'Borrador' },
  pendiente_pago: { bg: '#FEF3C7', color: '#78350F', label: 'Pendiente pago' },
  pagado:         { bg: '#DBEAFE', color: '#1E40AF', label: 'Pagado' },
  en_armado:      { bg: '#E0E7FF', color: '#3730A3', label: 'En armado' },
  en_ruta:        { bg: '#CFFAFE', color: '#0E7490', label: 'En ruta' },
  entregado:      { bg: '#D1FAE5', color: '#065F46', label: 'Entregado' },
  cancelado:      { bg: '#FEE2E2', color: '#991B1B', label: 'Cancelado' },
  rechazado:      { bg: '#FEE2E2', color: '#991B1B', label: 'Rechazado' },
}

const fmtDate = d => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' }) : '—'
const fmt = n => '$' + Math.round(Number(n || 0)).toLocaleString('es-UY')

export default function PedidosB2B() {
  const nav = useNavigate()
  const [pedidos, setPedidos] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState('activos') // activos | todos | borrador | pendiente_pago | en_armado | cancelado
  const [busqueda, setBusqueda] = useState('')
  const [accion, setAccion] = useState(null)
  const [mostrarArchivados, setMostrarArchivados] = useState(false)
  const [facturadoIds, setFacturadoIds] = useState(() => new Set())  // pedido_ids con CFE emitido
  const [facturarPedido, setFacturarPedido] = useState(null)  // pedido en modal de facturación

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    const { data, error } = await supabase
      .from('pedidos')
      .select(`
        id, numero_orden, estado, tipo, empresa_id, presupuesto_id,
        comprador_nombre, comprador_telefono, comprador_email,
        monto_subtotal, costo_envio, monto_total,
        fecha_entrega_programada, entrega_ventana, entrega_direccion, entrega_apto,
        metodo_pago, notas, created_at,
        facturacion_tipo, facturacion_razon_social, facturacion_rut,
        empresa:empresas!pedidos_empresa_id_fkey(id, razon_social, nombre_fantasia, telefono, email_facturacion, contacto_principal_nombre)
      `)
      .not('empresa_id', 'is', null)
      .order('created_at', { ascending: false })
    if (error) console.error(error)
    setPedidos(data || [])
    // "Facturado" = existe un CFE con pedido_id (no hay factura_id en pedidos; el link es inverso).
    const ids = (data || []).map(p => p.id)
    if (ids.length) {
      const { data: fe } = await supabase.from('facturas_emitidas').select('pedido_id').in('pedido_id', ids)
      setFacturadoIds(new Set((fe || []).map(f => f.pedido_id)))
    } else {
      setFacturadoIds(new Set())
    }
    setLoading(false)
  }

  // Definiciones (confirmar criterio de cierre con Roberto contable):
  //   facturado  = tiene CFE emitido (facturas_emitidas.pedido_id).
  //   cerrado    = ciclo completo: entregado + facturado + cobrado (pagado_at) → archivable.
  //   muerto     = cancelado / rechazado.
  //   ACTIVO     = ni muerto ni cerrado. ⇒ entregado sin facturar SIGUE activo comercial.
  const facturado = (p) => facturadoIds.has(p.id)
  const esCerrado = (p) => p.estado === 'entregado' && facturado(p) && !!p.pagado_at
  const esMuerto = (p) => ['cancelado', 'rechazado'].includes(p.estado)
  const esActivo = (p) => !esMuerto(p) && !esCerrado(p)

  const contadores = useMemo(() => {
    const c = { activos: 0, borrador: 0, pendiente_pago: 0, pagado: 0, en_armado: 0, en_ruta: 0, entregado: 0, cancelado: 0, rechazado: 0 }
    for (const p of pedidos) {
      c[p.estado] = (c[p.estado] || 0) + 1
      if (esActivo(p)) c.activos++
    }
    return c
  }, [pedidos, facturadoIds])

  const pedidosFiltrados = useMemo(() => {
    let r = pedidos
    if (filtroEstado === 'activos') r = r.filter(esActivo)
    else if (filtroEstado !== 'todos') r = r.filter(p => p.estado === filtroEstado)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase().trim()
      r = r.filter(p => {
        return (p.numero_orden || '').toLowerCase().includes(q)
          || (p.empresa?.nombre_fantasia || '').toLowerCase().includes(q)
          || (p.empresa?.razon_social || '').toLowerCase().includes(q)
          || (p.comprador_nombre || '').toLowerCase().includes(q)
          || (p.facturacion_razon_social || '').toLowerCase().includes(q)
      })
    }
    return r
  }, [pedidos, filtroEstado, busqueda, facturadoIds])

  // Lista principal a renderizar + sección "Archivados" (solo bajo la vista "Activos").
  const activos = pedidosFiltrados
  const archivados = filtroEstado === 'activos'
    ? pedidos.filter(p => esMuerto(p) || esCerrado(p))
    : []

  // KPIs — independientes del chip de filtro seleccionado.
  const kpiActivos = useMemo(() => pedidos.filter(esActivo), [pedidos, facturadoIds])
  const porFacturar = useMemo(() => pedidos.filter(p => p.estado === 'entregado' && !facturado(p)), [pedidos, facturadoIds])
  const facturacionPendiente = porFacturar.reduce((s, p) => s + Number(p.monto_total || 0), 0)
  const empresasActivas = new Set(kpiActivos.map(p => p.empresa_id)).size

  return (
    <AdminLayout>
      <div className="page">
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <h1 style={{ margin: 0, color: '#1A3461', fontSize: 22 }}>💼 Pedidos B2B</h1>
            <p style={{ margin: '4px 0 0', color: '#64748B', fontSize: 13 }}>
              Gestión comercial de pedidos empresariales · cotización, cobro, armado.
              <Link to="/admin/pedidos" style={{ marginLeft: 8, color: '#0369A1', textDecoration: 'none' }}>Ver pedidos B2C (control)</Link>
            </p>
          </div>
          <button
            onClick={() => nav('/admin/empresas')}
            style={{ background: '#1A3461', color: '#fff', border: 0, borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
          >+ Nuevo pedido</button>
        </div>

        {/* Filtros: chips estado + búsqueda */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 12, marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {[
              { v: 'activos', label: 'Activos', n: contadores.activos },
              { v: 'borrador', label: '📝 Borrador', n: contadores.borrador || 0 },
              { v: 'pendiente_pago', label: '⏳ Pendiente pago', n: contadores.pendiente_pago || 0 },
              { v: 'en_armado', label: '📦 En armado', n: contadores.en_armado || 0 },
              { v: 'entregado', label: '✅ Entregado', n: contadores.entregado || 0 },
              { v: 'cancelado', label: '❌ Cancelado', n: contadores.cancelado || 0 },
              { v: 'todos', label: 'Todos', n: pedidos.length },
            ].map(c => (
              <button
                key={c.v}
                onClick={() => setFiltroEstado(c.v)}
                style={{
                  padding: '5px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  background: filtroEstado === c.v ? '#DBEAFE' : '#fff',
                  color: filtroEstado === c.v ? '#1E40AF' : '#64748B',
                  border: filtroEstado === c.v ? '1px solid #1E40AF' : '1px solid #E2E8F0',
                }}
              >{c.label} <span style={{ opacity: 0.7 }}>· {c.n}</span></button>
            ))}
          </div>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' }}>Pedidos activos</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1A3461', marginTop: 3 }}>{kpiActivos.length}</div>
            <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2 }}>{contadores.borrador || 0} borrador · {contadores.pendiente_pago || 0} pend. pago · {contadores.en_armado || 0} armado · {porFacturar.length} por facturar</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' }}>Facturación pendiente</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#00A8B5', marginTop: 3 }}>{fmt(facturacionPendiente)}</div>
            <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2 }}>{porFacturar.length} entregado{porFacturar.length !== 1 ? 's' : ''} sin facturar</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' }}>Empresas activas</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1A3461', marginTop: 3 }}>{empresasActivas}</div>
            <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2 }}>con pedidos en curso</div>
          </div>
        </div>

        {/* Búsqueda */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, alignItems: 'center' }}>
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="🔍 Buscar por # / cliente / razón social / nombre fantasía"
            style={{ flex: 1, padding: '7px 12px', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: 12.5, background: '#fff', fontFamily: 'inherit' }}
          />
          {busqueda && (
            <button onClick={() => setBusqueda('')} style={{ background: 'transparent', border: '1px solid #CBD5E1', borderRadius: 8, padding: '5px 10px', fontSize: 11, color: '#64748B', cursor: 'pointer', fontFamily: 'inherit' }}>× Limpiar</button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>Cargando...</div>
        ) : activos.length === 0 && archivados.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748B', background: '#F8F6F1', borderRadius: 10 }}>
            <div style={{ fontSize: 40 }}>💼</div>
            <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600 }}>No hay pedidos B2B con los filtros actuales</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Los pedidos se crean desde la ficha de cada empresa (presupuesto → convertir a pedido).</div>
          </div>
        ) : (
          <>
            {activos.map(p => <PedidoCard key={p.id} p={p} onAccion={setAccion} facturado={facturado(p)} onFacturar={setFacturarPedido} />)}

            {archivados.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 11.5, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, margin: '14px 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 15 }}>🗄</span>
                  Archivados <span style={{ color: '#94A3B8', fontWeight: 400, textTransform: 'none' }}>· {archivados.length}</span>
                  <button onClick={() => setMostrarArchivados(!mostrarArchivados)} style={{ marginLeft: 'auto', background: 'transparent', border: 0, color: '#0369A1', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {mostrarArchivados ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>
                {mostrarArchivados && archivados.map(p => <PedidoCard key={p.id} p={p} onAccion={setAccion} facturado={facturado(p)} onFacturar={setFacturarPedido} archivado />)}
              </div>
            )}
          </>
        )}

        {accion && (
          <ModalAccion accion={accion} onClose={() => setAccion(null)} onDone={() => { setAccion(null); cargar() }} />
        )}
        {facturarPedido && (
          <ModalFacturar pedido={facturarPedido} onClose={() => setFacturarPedido(null)} onDone={() => { setFacturarPedido(null); cargar() }} />
        )}
      </div>
    </AdminLayout>
  )
}

function PedidoCard({ p, onAccion, archivado, facturado, onFacturar }) {
  const pill = ESTADO_PILL[p.estado] || ESTADO_PILL.borrador
  const nombre = p.empresa?.nombre_fantasia || p.empresa?.razon_social || p.facturacion_razon_social || p.comprador_nombre
  const razonSocial = p.empresa?.razon_social || p.facturacion_razon_social
  const contacto = p.empresa?.contacto_principal_nombre || p.comprador_nombre
  const nItems = (() => { try { return (p.items || (typeof p.items === 'string' ? JSON.parse(p.items) : []))?.length || 0 } catch { return 0 } })()

  return (
    <div style={{
      background: archivado ? '#F8F6F1' : '#fff',
      opacity: archivado ? 0.7 : 1,
      border: '1px solid #E2E8F0',
      borderRadius: 12,
      padding: '14px 16px',
      marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 11.5, color: '#0369A1', fontWeight: 700 }}>{p.numero_orden}</span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#DBEAFE', color: '#1E40AF' }}>B2B</span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: pill.bg, color: pill.color, textTransform: 'uppercase' }}>{pill.label}</span>
        {p.presupuesto_id && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#EDE9FE', color: '#5B21B6' }}>📄 desde presupuesto</span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 800, color: '#1A3461' }}>{fmt(p.monto_total)}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr auto', gap: 14, alignItems: 'center' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#1A3461' }}>{nombre}</div>
          {razonSocial && razonSocial !== nombre && (
            <div style={{ fontSize: 11, color: '#94A3B8', fontStyle: 'italic' }}>Razón social: {razonSocial}</div>
          )}
          {contacto && (
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>👤 {contacto}{p.comprador_telefono ? ` · 📱 ${p.comprador_telefono}` : ''}</div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#64748B' }}>📅 Entrega</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1A3461' }}>{fmtDate(p.fecha_entrega_programada)}</div>
          <div style={{ fontSize: 11, color: '#64748B' }}>{labelVentana(p.entrega_ventana)}</div>
          {p.entrega_direccion && (
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📍 {p.entrega_direccion}{p.entrega_apto ? ` · ${p.entrega_apto}` : ''}</div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'stretch' }}>
          {p.estado === 'borrador' && (
            <>
              <button
                onClick={() => onAccion({ tipo: 'adelantar_estado', pedido: p })}
                title="Adelantar el pedido a en_armado o entregado sin registrar pago (empresas pagan post-entrega)"
                style={{ background: '#1A3461', color: '#fff', border: 0, borderRadius: 8, padding: '7px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                📦 Enviar a armar / entregar
              </button>
              <button onClick={() => onAccion({ tipo: 'marcar_pagado', pedido: p })} style={{ background: 'transparent', color: '#16A34A', border: '1px solid #16A34A', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                ✓ Marcar pagado (cobro adelantado)
              </button>
            </>
          )}
          {p.estado === 'pendiente_pago' && (
            <button onClick={() => onAccion({ tipo: 'marcar_pagado', pedido: p })} style={{ background: '#16A34A', color: '#fff', border: 0, borderRadius: 8, padding: '7px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              ✓ Marcar pagado
            </button>
          )}
          {p.estado === 'en_armado' && (
            <Link to="/admin/entregas" style={{ background: '#DBEAFE', color: '#1E40AF', border: 0, borderRadius: 8, padding: '7px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', textAlign: 'center', textDecoration: 'none', fontFamily: 'inherit' }}>
              📦 Armar en Entregas →
            </Link>
          )}
          {p.estado === 'entregado' && !facturado && (
            <button onClick={() => onFacturar(p)} style={{ background: '#16A34A', color: '#fff', border: 0, borderRadius: 8, padding: '7px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              🧾 Facturar (e-Factura)
            </button>
          )}
          {facturado && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'center', fontSize: 11, fontWeight: 700, padding: '6px 10px', borderRadius: 8, background: '#DCFCE7', color: '#16A34A' }}>
              🧾 CFE emitido
            </span>
          )}
          {!archivado && !['cancelado', 'rechazado', 'entregado'].includes(p.estado) && (
            <button onClick={() => onAccion({ tipo: 'cancelar', pedido: p })} style={{ background: 'transparent', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancelar
            </button>
          )}
        </div>
      </div>

      {p.notas && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#FEF3C7', borderRadius: 8, fontSize: 12, color: '#78350F' }}>📝 {p.notas}</div>
      )}
    </div>
  )
}

function ModalAccion({ accion, onClose, onDone }) {
  const { tipo, pedido } = accion
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [referencia, setReferencia] = useState('')
  const [motivo, setMotivo] = useState('')
  // Para "adelantar_estado": target puede ser 'en_armado' o 'entregado'
  const [targetEstado, setTargetEstado] = useState('en_armado')

  async function ok() {
    setBusy(true); setErr(null)
    try {
      if (tipo === 'marcar_pagado') {
        await adminMarcarPedidoPagado(pedido.id, referencia || null)
      } else if (tipo === 'cancelar') {
        if (!confirm(`¿Cancelar ${pedido.numero_orden}?`)) { setBusy(false); return }
        await adminCancelarPedido(pedido.id, motivo || null)
      } else if (tipo === 'adelantar_estado') {
        // Flujo B2B: empresa paga post-entrega. Salteamos pendiente_pago y pasamos
        // borrador → en_armado o borrador → entregado directamente.
        const nota = `\n\n[${new Date().toISOString().slice(0,10)}] Adelantado a ${targetEstado} sin pago previo (cobro post-entrega)${motivo ? ': ' + motivo : ''}`
        const patch = { estado: targetEstado, notas: (pedido.notas || '') + nota }
        if (targetEstado === 'entregado') {
          patch.entregado_at = new Date().toISOString()
        }
        const { error } = await supabase
          .from('pedidos')
          .update(patch)
          .eq('id', pedido.id)
          .eq('estado', 'borrador') // safety: solo desde borrador
        if (error) throw error
      }
      onDone()
    } catch (e) {
      setErr(e.message)
      setBusy(false)
    }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, maxWidth: 460, width: '100%', padding: 24 }}>
        <h2 style={{ margin: 0, fontSize: 17, color: '#1A3461', fontWeight: 800, marginBottom: 14 }}>
          {tipo === 'marcar_pagado' ? `✓ Confirmar pago · ${pedido.numero_orden}`
            : tipo === 'adelantar_estado' ? `📦 Adelantar pedido · ${pedido.numero_orden}`
            : `❌ Cancelar ${pedido.numero_orden}`}
        </h2>
        <div style={{ background: '#F8F6F1', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 12.5 }}>
          <div><strong>Cliente:</strong> {pedido.empresa?.nombre_fantasia || pedido.empresa?.razon_social || pedido.comprador_nombre}</div>
          <div><strong>Total:</strong> {fmt(pedido.monto_total)}</div>
        </div>
        {tipo === 'marcar_pagado' && (
          <label style={{ display: 'block', marginBottom: 14 }}>
            <b style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#1A3461', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Referencia (nro transferencia, ticket, etc.)</b>
            <input type="text" value={referencia} onChange={e => setReferencia(e.target.value)} placeholder="ej: TR-2026-0123" style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
          </label>
        )}
        {tipo === 'cancelar' && (
          <label style={{ display: 'block', marginBottom: 14 }}>
            <b style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#1A3461', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Motivo (opcional)</b>
            <textarea rows="2" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Cliente pidió cancelar, sin pago, etc." style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
          </label>
        )}
        {tipo === 'adelantar_estado' && (
          <>
            <div style={{ padding: '10px 12px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, marginBottom: 12, fontSize: 12.5, color: '#1E3A8A' }}>
              El pedido pasa al estado elegido <b>sin registrar pago</b>. Cobrás después (transferencia, e-Factura crédito, etc). Cuando quede en <b>entregado</b> podés facturarlo desde acá.
            </div>
            <div style={{ marginBottom: 14 }}>
              <b style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#1A3461', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>¿A qué estado?</b>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'flex', gap: 10, padding: '10px 12px', border: `1.5px solid ${targetEstado === 'en_armado' ? '#1A3461' : '#E2E8F0'}`, borderRadius: 8, cursor: 'pointer', background: targetEstado === 'en_armado' ? '#F1F5F9' : '#fff' }}>
                  <input type="radio" name="target_estado" value="en_armado" checked={targetEstado === 'en_armado'} onChange={() => setTargetEstado('en_armado')} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1A3461' }}>📦 En armado</div>
                    <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 2 }}>Entra a la cola de armado. Después vas a Entregas para marcarlo entregado.</div>
                  </div>
                </label>
                <label style={{ display: 'flex', gap: 10, padding: '10px 12px', border: `1.5px solid ${targetEstado === 'entregado' ? '#1A3461' : '#E2E8F0'}`, borderRadius: 8, cursor: 'pointer', background: targetEstado === 'entregado' ? '#F1F5F9' : '#fff' }}>
                  <input type="radio" name="target_estado" value="entregado" checked={targetEstado === 'entregado'} onChange={() => setTargetEstado('entregado')} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1A3461' }}>✅ Entregado</div>
                    <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 2 }}>Ya se entregó, saltar armado y ruta. Queda listo para facturar.</div>
                  </div>
                </label>
              </div>
            </div>
            <label style={{ display: 'block', marginBottom: 14 }}>
              <b style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#1A3461', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Nota (opcional)</b>
              <input type="text" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="ej: cobro contra entrega · cuenta corriente 30 días" style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
            </label>
          </>
        )}
        {err && <div style={{ padding: 8, background: '#FEE2E2', color: '#991B1B', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'transparent', color: '#475569', border: '1.5px solid #E2E8F0', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Cerrar</button>
          <button
            onClick={ok}
            disabled={busy}
            style={{ background: tipo === 'cancelar' ? '#DC2626' : tipo === 'adelantar_estado' ? '#1A3461' : '#16A34A', color: '#fff', border: 0, borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
          >
            {busy ? 'Procesando…'
              : tipo === 'cancelar' ? 'Sí, cancelar'
              : tipo === 'adelantar_estado' ? (targetEstado === 'entregado' ? 'Marcar entregado' : 'Enviar a armar')
              : 'Confirmar pago'}
          </button>
        </div>
      </div>
    </div>
  )
}

const IVA_LABEL = { 1: 'IVA 0%', 2: 'IVA 10%', 3: 'IVA 22%' }

/**
 * Bloque visual de error humano-legible. Recibe el objeto que devuelve traducirErrorEmision().
 * Muestra: título grande + detalle + CTA (opcional) + detalles técnicos colapsables.
 */
function BloqueError({ err, pedido, compacto }) {
  const [verTecnico, setVerTecnico] = useState(false)
  if (!err) return null
  return (
    <div style={{
      padding: compacto ? '10px 12px' : '14px 16px',
      background: '#FEE2E2',
      color: '#7F1D1D',
      border: '1px solid #FCA5A5',
      borderRadius: 10,
      fontSize: 12.5,
      marginBottom: 14,
      lineHeight: 1.5,
    }}>
      <div style={{ fontWeight: 800, fontSize: compacto ? 13 : 14, color: '#991B1B', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 16, lineHeight: 1 }}>⚠</span>
        <span style={{ flex: 1 }}>{err.titulo}</span>
      </div>
      <div style={{ marginTop: 6, marginLeft: 24 }}>{err.detalle}</div>
      {err.cta && pedido?.empresa?.id && (
        <div style={{ marginTop: 10, marginLeft: 24 }}>
          <a
            href={`/admin/empresas/${pedido.empresa.id}`}
            style={{
              display: 'inline-block',
              background: '#991B1B',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 11.5,
              fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            → {err.cta.label}
          </a>
        </div>
      )}
      {err.tecnico && err.tecnico !== err.detalle && (
        <div style={{ marginTop: 8, marginLeft: 24 }}>
          <button
            type="button"
            onClick={() => setVerTecnico(v => !v)}
            style={{
              background: 'transparent',
              border: 0,
              color: '#991B1B',
              fontSize: 10.5,
              textDecoration: 'underline',
              cursor: 'pointer',
              padding: 0,
              opacity: 0.75,
            }}
          >
            {verTecnico ? 'Ocultar detalle técnico' : 'Ver detalle técnico'}
          </button>
          {verTecnico && (
            <pre style={{
              marginTop: 6,
              padding: 8,
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 6,
              fontSize: 10.5,
              color: '#450A0A',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 160,
              overflowY: 'auto',
            }}>{err.tecnico}</pre>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Traduce el error crudo de emitir-cfe / Biller a un mensaje humano + acción sugerida.
 * Le decís: `data` (el body que devolvió la fn, si lo tenemos) y `errorRaw` (el string
 * genérico del SDK, tipo "Edge Function returned a non-2xx status code").
 * Devuelve `{ titulo, detalle, cta: { label, action } | null, tecnico }`.
 */
function traducirErrorEmision(data, errorRaw) {
  const bruto = String(data?.biller_error || data?.error || errorRaw || '')
  const payload = data?.payload_enviado

  // Ambiente Biller no configurado
  if (data?.error === 'BILLER_NOT_CONFIGURED' || /BILLER_NOT_CONFIGURED/.test(bruto)) {
    return {
      titulo: 'Biller no está configurado',
      detalle: 'Faltan las variables BILLER_API_TOKEN o BILLER_SUCURSAL_ID en el sistema. Un admin tiene que cargarlas antes de facturar.',
      cta: null,
      tecnico: bruto,
    }
  }
  // Email inválido según Biller (bug con TLDs .com.uy y similares)
  if (/ClientesSucursales\[emails\]|Email no es una dirección de correo válida/i.test(bruto)) {
    const email = payload?.cliente?.sucursal?.emails?.[0] || 'del receptor'
    return {
      titulo: 'Email de facturación no aceptado por Biller',
      detalle: `Biller rechazó el email "${email}". Suele pasar con dominios tipo .com.uy o .net.uy. Cambialo por uno simple (.com, .uy, .gmail.com) desde la ficha de la empresa.`,
      cta: { label: 'Editar empresa', action: 'editar_empresa' },
      tecnico: bruto,
    }
  }
  // RUT o CI inválido
  if (/documento inválido|documento no válido|invalid document|RUT no válido/i.test(bruto)) {
    return {
      titulo: 'RUT / CI del receptor no es válido',
      detalle: 'Biller rechazó el número de documento del receptor. Revisá que el RUT tenga 12 dígitos o la CI 7-8 dígitos, sin puntos ni guiones.',
      cta: { label: 'Editar empresa', action: 'editar_empresa' },
      tecnico: bruto,
    }
  }
  // Pedido en estado incorrecto
  if (data?.error === 'pedido B2B aún no entregado') {
    return {
      titulo: 'El pedido todavía no está entregado',
      detalle: `Estado actual: "${data?.estado_actual || '?'}". Marcalo como "entregado" o "en_ruta" antes de facturar.`,
      cta: null,
      tecnico: bruto,
    }
  }
  if (data?.error === 'pedido no está pagado') {
    return {
      titulo: 'El pedido todavía no está pagado',
      detalle: 'Registrá el pago antes de facturar.',
      cta: null,
      tecnico: bruto,
    }
  }
  if (data?.error === 'pedido sin items') {
    return {
      titulo: 'El pedido no tiene items',
      detalle: 'No hay líneas para facturar. Agregá productos al pedido antes de emitir.',
      cta: null,
      tecnico: bruto,
    }
  }
  if (data?.error === 'pedido no encontrado' || data?.error === 'pago no encontrado') {
    return {
      titulo: 'El pedido ya no existe',
      detalle: 'Puede haber sido eliminado por otro usuario. Refrescá la lista.',
      cta: null,
      tecnico: bruto,
    }
  }
  // Biller caído / rate limit / timeout
  if (data?.biller_status === 500 || data?.biller_status === 502 || data?.biller_status === 503) {
    return {
      titulo: 'Biller está caído o no responde',
      detalle: `HTTP ${data.biller_status}. Reintentá en 1-2 minutos. Si sigue fallando, revisá status.biller.uy.`,
      cta: null,
      tecnico: bruto,
    }
  }
  if (data?.biller_status === 401 || data?.biller_status === 403) {
    return {
      titulo: 'Token de Biller vencido o inválido',
      detalle: 'Un admin tiene que regenerar BILLER_API_TOKEN y actualizarlo en las variables del sistema.',
      cta: null,
      tecnico: bruto,
    }
  }
  if (data?.biller_status === 429) {
    return {
      titulo: 'Biller nos limitó por muchos requests',
      detalle: 'Esperá 30 segundos y reintentá.',
      cta: null,
      tecnico: bruto,
    }
  }
  // Rechazo genérico de Biller — con detalle si viene
  if (data?.error === 'Biller rechazó el CFE' && data?.biller_error) {
    return {
      titulo: 'Biller rechazó el CFE',
      detalle: `Detalle de Biller: ${data.biller_error.slice(0, 300)}`,
      cta: null,
      tecnico: bruto,
    }
  }
  // Falla al insertar en DB pero el CFE se emitió
  if (/CFE emitido pero error guardando/.test(bruto) || data?.warning) {
    return {
      titulo: 'CFE emitido pero no lo guardé en el sistema',
      detalle: `El comprobante ${data?.biller_cfe_id || ''} sí se creó en Biller. Contactá al admin para reconciliarlo manualmente. NO reintentes o vas a emitir duplicado.`,
      cta: null,
      tecnico: bruto,
    }
  }
  // Fallback genérico
  return {
    titulo: 'No pude emitir el CFE',
    detalle: bruto || 'Error desconocido. Reintentá o consultá al admin si persiste.',
    cta: null,
    tecnico: bruto,
  }
}

// CRÍTICO #3 — Modal de facturación B2B: preview (dry_run) → confirmar → emitir e-Factura.
function ModalFacturar({ pedido, onClose, onDone }) {
  const [preview, setPreview] = useState(null)
  const [loadingPrev, setLoadingPrev] = useState(true)
  const [err, setErr] = useState(null)
  const [emitiendo, setEmitiendo] = useState(false)
  const [resultado, setResultado] = useState(null)

  // Cuando la fn devuelve 4xx/5xx, supabase-js pone el body en error.context.
  // Lo leemos para que traducirErrorEmision() tenga la data real (biller_error, etc).
  async function invokeConBody(nombreFn, body) {
    const r = await supabase.functions.invoke(nombreFn, { body })
    if (r.error && r.error.context && typeof r.error.context.json === 'function') {
      try {
        const bodyErr = await r.error.context.json()
        return { data: bodyErr, error: r.error }
      } catch { /* fall through */ }
    }
    return r
  }

  useEffect(() => {
    let vivo = true
    ;(async () => {
      setLoadingPrev(true); setErr(null)
      const { data, error } = await invokeConBody('emitir-cfe', { dry_run: true, pedido_id: pedido.id })
      if (!vivo) return
      if (error || !data?.ok) {
        const t = traducirErrorEmision(data, error?.message)
        setErr(t)
      } else {
        setPreview(data)
      }
      setLoadingPrev(false)
    })()
    return () => { vivo = false }
  }, [pedido.id])

  const esFactura = preview?.tipo_comprobante === 111
  const warning = preview && !esFactura  // B2B siempre debería salir e-Factura (RUT de la empresa)

  async function emitir() {
    setEmitiendo(true); setErr(null)
    try {
      const { data, error } = await invokeConBody('emitir-cfe', { pedido_id: pedido.id })
      if (error || !data?.ok) {
        const t = traducirErrorEmision(data, error?.message)
        setErr(t)
        setEmitiendo(false)
        return
      }
      setResultado(data)
      // Log best-effort (no bloquea si RLS lo rechaza)
      try {
        const { data: u } = await supabase.auth.getUser()
        await supabase.from('admin_actions_log').insert({
          admin_user_id: u?.user?.id || null, target_type: 'pedido', target_id: pedido.id,
          action: 'facturar', motivo: 'Facturación manual desde panel B2B',
          metadata: { pedido_id: pedido.id, numero_orden: pedido.numero_orden, biller_cfe_id: data.biller_cfe_id, tipo_comprobante: data.tipo_comprobante, serie: data.serie, numero: data.numero },
        })
      } catch { /* noop */ }
    } catch (e) {
      // Fallback si tirara excepción no manejada (network, etc.)
      setErr(traducirErrorEmision(null, e?.message))
      setEmitiendo(false)
    }
  }

  const cliente = pedido.empresa?.nombre_fantasia || pedido.empresa?.razon_social || pedido.comprador_nombre

  return (
    <div onClick={resultado ? onDone : onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, maxWidth: 520, width: '100%', padding: 24, maxHeight: '88vh', overflowY: 'auto' }}>
        <h2 style={{ margin: 0, fontSize: 17, color: '#1A3461', fontWeight: 800, marginBottom: 4 }}>🧾 Facturar · {pedido.numero_orden}</h2>
        <div style={{ fontSize: 12.5, color: '#64748B', marginBottom: 14 }}>{cliente}</div>

        {resultado ? (
          <div>
            <div style={{ background: '#DCFCE7', border: '1px solid #16A34A', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
              <div style={{ fontWeight: 800, color: '#14532D', fontSize: 14 }}>✅ CFE emitido</div>
              <div style={{ fontSize: 12.5, color: '#14532D', marginTop: 6 }}>
                {resultado.tipo_comprobante === 111 ? 'e-Factura' : 'e-Ticket'} · serie {resultado.serie} · N° {resultado.numero}<br />
                Biller ID: <code>{resultado.biller_cfe_id}</code><br />
                Estado DGI: {resultado.tipo_comprobante === 111 ? 'Pendiente DGI (se reporta a DGI)' : 'Envío no corresponde'}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onDone} style={{ background: '#1A3461', color: '#fff', border: 0, borderRadius: 8, padding: '9px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Listo</button>
            </div>
          </div>
        ) : loadingPrev ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#64748B' }}>Generando preview del CFE…</div>
        ) : err && !preview ? (
          <div>
            <BloqueError err={err} pedido={pedido} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ background: 'transparent', color: '#475569', border: '1.5px solid #E2E8F0', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Cerrar</button>
            </div>
          </div>
        ) : preview ? (
          <div>
            {warning && (
              <div style={{ padding: '10px 14px', background: '#FEF9C3', border: '1px solid #CA8A04', color: '#78350F', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>
                ⚠ Saldría <b>e-Ticket</b> (sin RUT), no e-Factura. Revisá que la empresa tenga RUT antes de emitir.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <div style={{ background: esFactura ? '#DCFCE7' : '#FEF9C3', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 10, color: '#64748B', textTransform: 'uppercase', fontWeight: 700 }}>Tipo</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: esFactura ? '#16A34A' : '#CA8A04' }}>{preview.tipo_label} ({preview.tipo_comprobante})</div>
              </div>
              <div style={{ background: '#F8FAFC', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 10, color: '#64748B', textTransform: 'uppercase', fontWeight: 700 }}>Receptor</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1A3461' }}>{preview.receptor?.razon_social || '—'}</div>
                <div style={{ fontSize: 11, color: '#64748B' }}>{preview.receptor?.documento ? `${preview.receptor.tipo_documento === 2 ? 'RUT' : 'CI'} ${preview.receptor.documento}` : (preview.receptor?.nota || 'sin documento')}</div>
              </div>
            </div>
            <div style={{ border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
              {preview.lineas.map((l, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '7px 12px', borderTop: i ? '1px solid #F1F5F9' : 'none', fontSize: 12 }}>
                  <span style={{ color: '#1A3461', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.concepto} <span style={{ color: '#94A3B8', fontSize: 10.5 }}>· {IVA_LABEL[l.indicador_facturacion] || ''}</span></span>
                  <span style={{ color: '#475569', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{fmt(l.precio)}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 18, fontSize: 12.5, marginBottom: 16 }}>
              <span style={{ color: '#64748B' }}>Subtotal {fmt(preview.subtotal)}</span>
              <span style={{ color: '#64748B' }}>IVA {fmt(preview.iva_total)}</span>
              <span style={{ color: '#1A3461', fontWeight: 800 }}>Total {fmt(preview.monto_total)}</span>
            </div>
            {err && <BloqueError err={err} pedido={pedido} compacto />}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} disabled={emitiendo} style={{ background: 'transparent', color: '#475569', border: '1.5px solid #E2E8F0', borderRadius: 8, padding: '9px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={emitir} disabled={emitiendo} style={{ background: '#16A34A', color: '#fff', border: 0, borderRadius: 8, padding: '9px 16px', fontSize: 12.5, fontWeight: 700, cursor: emitiendo ? 'wait' : 'pointer' }}>
                {emitiendo ? 'Emitiendo…' : `Emitir ${preview.tipo_label}`}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
